import { useCallback, useEffect, useMemo, useState } from "react";
import useAgentStore from "../useAgentStore";

import { prepareAvatarUploadFile } from "./avatarImageUtils";

const AVATAR_ACCEPT = {
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/webp": [".webp"],
  "image/gif": [".gif"],
};

export function useAgentAvatarUpload({
  agentId,
  isCreateMode,
  isSystemAgent,
  avatarUrl,
  setAvatarUrl,
}) {
  const [pendingAvatarFile, setPendingAvatarFile] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarError, setAvatarError] = useState("");

  const pendingPreviewUrl = useMemo(() => {
    if (!pendingAvatarFile) return null;
    return URL.createObjectURL(pendingAvatarFile);
  }, [pendingAvatarFile]);

  useEffect(() => {
    return () => {
      if (pendingPreviewUrl) {
        URL.revokeObjectURL(pendingPreviewUrl);
      }
    };
  }, [pendingPreviewUrl]);

  const displayAvatarUrl = pendingPreviewUrl || avatarUrl || null;
  const hasCustomAvatar = Boolean(displayAvatarUrl);
  const canEditAvatar = !isSystemAgent;

  const uploadAvatarForAgent = useCallback(async (targetAgentId, file) => {
    const updated = await useAgentStore.getState().uploadAgentAvatar(targetAgentId, file);
    if (updated?.avatarUrl) {
      setAvatarUrl(updated.avatarUrl);
    }
    return updated;
  }, [setAvatarUrl]);

  const handleSelectAvatarFile = useCallback(
    async (file) => {
      if (!file || !canEditAvatar) return;
      setAvatarError("");

      setUploadingAvatar(true);
      try {
        // prepareAvatarUploadFile is the single validator for type/size.
        const preparedFile = await prepareAvatarUploadFile(file);

        if (isCreateMode || !agentId) {
          setPendingAvatarFile(preparedFile);
          setAvatarUrl("");
          return;
        }

        await uploadAvatarForAgent(agentId, preparedFile);
        setPendingAvatarFile(null);
      } catch (error) {
        setAvatarError(error?.message || "Failed to upload avatar.");
      } finally {
        setUploadingAvatar(false);
      }
    },
    [agentId, canEditAvatar, isCreateMode, setAvatarUrl, uploadAvatarForAgent]
  );

  const handleRemoveAvatar = useCallback(async () => {
    if (!canEditAvatar) return;
    setAvatarError("");
    setPendingAvatarFile(null);
    setAvatarUrl("");

    if (isCreateMode || !agentId || !avatarUrl) {
      return;
    }

    setUploadingAvatar(true);
    try {
      const updated = await useAgentStore.getState().clearAgentAvatar(agentId);
      if (updated) {
        setAvatarUrl(updated.avatarUrl || "");
      }
    } catch (error) {
      setAvatarError(error?.message || "Failed to remove avatar.");
    } finally {
      setUploadingAvatar(false);
    }
  }, [agentId, avatarUrl, canEditAvatar, isCreateMode, setAvatarUrl]);

  const flushPendingAvatarUpload = useCallback(
    async (targetAgentId) => {
      if (!pendingAvatarFile || !targetAgentId) return null;
      setUploadingAvatar(true);
      try {
        const updated = await uploadAvatarForAgent(targetAgentId, pendingAvatarFile);
        setPendingAvatarFile(null);
        return updated;
      } catch (error) {
        setAvatarError(error?.message || "Failed to upload avatar.");
        throw error;
      } finally {
        setUploadingAvatar(false);
      }
    },
    [pendingAvatarFile, uploadAvatarForAgent]
  );

  const resetAvatarUploadState = useCallback(() => {
    setPendingAvatarFile(null);
    setAvatarError("");
    setUploadingAvatar(false);
  }, []);

  return {
    displayAvatarUrl,
    hasCustomAvatar,
    canEditAvatar,
    uploadingAvatar,
    avatarError,
    handleSelectAvatarFile,
    handleRemoveAvatar,
    flushPendingAvatarUpload,
    resetAvatarUploadState,
    avatarAccept: AVATAR_ACCEPT,
  };
}
